import { Link } from "react-router-dom";
import { Eyebrow } from "@/components/common";
import { IMAGES } from "@/data/content";

const PAGES = {
  about: {
    eyebrow: "The Institution",
    title: "About INDIC",
    img: IMAGES.templeTower,
    lead: "INDIC is a digital institution for studying Indic civilisation — a product of Mahakali Tribunal, guided by its principles of Intelligence, Justice, and Wealth.",
    blocks: [
      { h: "Our mission", p: "To become a globally accessible digital institution for studying Indic civilisation — preserving historical knowledge while encouraging intellectual freedom, and connecting history, philosophy, literature, science, geography, and cultural heritage through a common knowledge architecture." },
      { h: "Three layers", p: "INDIC works across three layers: Civilisation (history, archaeology, science, literature, culture, and governance); Philosophy & Traditions (Buddhism, Shaktism, Shaivism, Sikhism, and other schools of thought); and Knowledge & Intelligence (books, research, multilingual learning, the knowledge graph, and INDIC Scholar)." },
      { h: "Free to read", p: "INDIC is primarily free to read. It exists as a public-interest knowledge mission, with room for sustainability through advanced research tools, educational programmes, institutional licensing, and scholarly publishing." },
      { h: "What you will find", p: "A cinematic gateway to the civilisation, a digital library of complete texts, an interactive atlas and timelines, a mandala of core concepts, critical studies, a learning academy, and INDIC Scholar — an AI research assistant grounded in the library." },
    ],
  },
  sources: {
    eyebrow: "Provenance",
    title: "Sources",
    img: IMAGES.templeCarved,
    lead: "INDIC is built on supplied archives, fully ingested and made readable in-page. We do not claim content has been imported until its records are actually available in the application.",
    blocks: [
      { h: "The Holy Book", p: "A structured philosophical scripture of 377 chapters across twelve Books — from Reality and Mind to Love, Society, and Death — together with its Opening Manifesto and The Book of Everything. Ingested from the supplied Markdown corpus." },
      { h: "Indic Civilisation", p: "A seven-volume scholarly compendium (over 2,600 pages) surveying the Indic civilisational field: its scope and method, history, philosophy, sciences, literature, and encounters with the world. Ingested and segmented into chapters and sections." },
      { h: "Kali's Letters to Humanity", p: "Eight volumes of contemplative letters (over 2,000 pages) — meditations on beginning again, courage, love, justice, and becoming unafraid. Ingested as 400 letters across eight parts." },
      { h: "Attribution", p: "Source attribution is preserved throughout. Each section records its originating work and part, and every reader page cites its source. Further traditions and repositories will be integrated in phases." },
    ],
  },
  "editorial-policy": {
    eyebrow: "How We Work",
    title: "Editorial Policy",
    img: IMAGES.mandalaColor,
    lead: "INDIC practises evidence-based scholarship with intellectual independence, respecting living cultural traditions without requiring religious adherence.",
    blocks: [
      { h: "Reality outranks authority", p: "No claim becomes true because it is ancient, revered, powerful, or repeated with confidence. Every teaching answers to evidence, consequence, conscience, and the dignity of living beings." },
      { h: "Fact, interpretation, opinion", p: "We distinguish clearly between established fact, scholarly interpretation, and opinion — and we revise when better evidence appears. Certainty is kept proportionate to the grounds for it." },
      { h: "Freedom of thought and religion", p: "We respect the traditions that have formed people, and we defend the right to inquire, doubt, compare, and disagree. Rigorous criticism of ideas and institutions never licenses contempt for people based on their identity." },
      { h: "Repair over purity", p: "Where we err, we name it plainly, correct the record, and improve. An open book for an unfinished people: understanding may be added without pretending to have escaped error." },
    ],
  },
  contribute: {
    eyebrow: "Join the Work",
    title: "Contribute",
    img: IMAGES.himalayaGreen,
    lead: "INDIC is an extensible, production-oriented platform built in phases. Scholars, translators, and readers are invited to help it grow.",
    blocks: [
      { h: "Scholarship", p: "Propose corrections, context, and critical essays. All contributions are reviewed against our editorial policy and must distinguish fact from interpretation." },
      { h: "Translation", p: "Help bring the library into Indian and world languages, with careful attention to scripts, line spacing, and the integrity of meaning." },
      { h: "Reading & feedback", p: "Create a free account, bookmark passages, and tell us what is unclear or missing. Readers shape what INDIC becomes." },
      { h: "Get in touch", p: "This is an early public build. Partnership, institutional licensing, and editorial collaboration enquiries are welcome via Mahakali Tribunal." },
    ],
  },
};

export default function EditorialPage({ page }) {
  const data = PAGES[page];
  if (!data) return null;
  return (
    <div className="pt-16 lg:pt-[72px]" data-testid={`editorial-${page}`}>
      <section className="relative py-24 lg:py-32 border-b border-indic-gold/15">
        <img src={data.img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1021]/75 to-indic-indigo" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-8">
          <Eyebrow>{data.eyebrow}</Eyebrow>
          <h1 className="mt-4 font-serif text-5xl lg:text-6xl font-semibold text-amber-50">{data.title}</h1>
          <p className="mt-6 text-xl text-slate-300/90 leading-relaxed">{data.lead}</p>
        </div>
      </section>
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-8 space-y-10">
          {data.blocks.map((b) => (
            <div key={b.h} className="border-l-2 border-indic-gold/40 pl-6">
              <h2 className="font-serif text-2xl text-amber-50">{b.h}</h2>
              <p className="mt-2 text-lg text-slate-300/90 leading-relaxed">{b.p}</p>
            </div>
          ))}
          <div className="pt-6">
            <Link to="/library" className="inline-block rounded-sm bg-indic-gold px-6 py-3 font-semibold text-indic-indigo hover:bg-indic-gold-soft transition-colors" data-testid="editorial-library-cta">Enter the Library →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
